import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTimes } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { Element, StopwatchStep } from "../../types";
import { generateId } from "../../utils/Utils";
import "../add-timer-modal/AddTimerModal.css";

interface AddStopwatchModalProps {
    isOpen: boolean;
    onClose: () => void;
    onAdd: (element: Element) => void;
}

const createEmptyStep = (): StopwatchStep => ({
    id: generateId(),
    text: '',
    timeHistory: [],
    completed: false,
});

export const AddStopwatchModal: React.FC<AddStopwatchModalProps> = ({ isOpen, onClose, onAdd }) => {
    const { t } = useTranslation();
    const [title, setTitle] = useState('');
    const [mode, setMode] = useState<'simple' | 'steps'>('simple');
    const [steps, setSteps] = useState<StopwatchStep[]>([createEmptyStep()]);

    if (!isOpen) return null;

    const closeAndReset = () => {
        setTitle('');
        setMode('simple');
        setSteps([createEmptyStep()]);
        onClose();
    };

    const handleSubmit = () => {
        const validSteps = steps.filter((step) => step.text.trim());
        if (!title.trim()) return;

        onAdd({
            id: generateId(),
            type: 'stopwatch',
            title: title.trim(),
            mode,
            steps: mode === 'simple'
                ? [{ ...createEmptyStep(), text: title.trim() }]
                : validSteps.length > 0
                    ? validSteps
                    : [{ ...createEmptyStep(), text: t('addStopwatch.defaultStep') }],
            completedDates: [],
            sessionHistory: [],
        });
        closeAndReset();
    };

    return (
        <div className="add-timer-modal-overlay" onClick={closeAndReset}>
            <div className="add-timer-modal" onClick={(event) => event.stopPropagation()}>
                <div className="add-timer-header">
                    <div>
                        <h3>{t('addStopwatch.title')}</h3>
                        <p>{mode === 'simple' ? t('addStopwatch.simple') : `${t('addStopwatch.steps')} ${steps.length}`}</p>
                    </div>
                    <button type="button" className="add-timer-close" onClick={closeAndReset} aria-label={t('addStopwatch.cancel')}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <div className="add-timer-field">
                    <label>{t('addStopwatch.name')}</label>
                    <input
                        className="add-timer-input"
                        value={title}
                        onChange={(event) => setTitle(event.target.value)}
                        placeholder={t('addStopwatch.namePlaceholder')}
                        autoFocus
                    />
                </div>

                <div className="element-mode-selector" role="group" aria-label={t('addStopwatch.mode')}>
                    <button type="button" className={mode === 'simple' ? 'active' : ''} onClick={() => setMode('simple')}>{t('addStopwatch.simple')}</button>
                    <button type="button" className={mode === 'steps' ? 'active' : ''} onClick={() => setMode('steps')}>{t('addStopwatch.withSteps')}</button>
                </div>

                {mode === 'steps' && <div className="add-timer-subtimers">
                    <div className="add-timer-section-heading">
                        <label>{t('addStopwatch.steps')}</label>
                        <span>{steps.length}</span>
                    </div>
                    {steps.map((step, index) => (
                        <div className="add-timer-subtimer-item" key={step.id}>
                            <span className="add-timer-subtimer-number">{index + 1}</span>
                            <input
                                className="add-timer-subtimer-input"
                                value={step.text}
                                onChange={(event) => setSteps((current) => current.map((item, itemIndex) => (
                                    itemIndex === index ? { ...item, text: event.target.value } : item
                                )))}
                                placeholder={t('addStopwatch.stepPlaceholder')}
                            />
                            {steps.length > 1 && (
                                <button
                                    type="button"
                                    className="add-timer-remove-button"
                                    onClick={() => setSteps((current) => current.filter((_, itemIndex) => itemIndex !== index))}
                                    aria-label={t('addStopwatch.removeStep')}
                                    title={t('addStopwatch.removeStep')}
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            )}
                        </div>
                    ))}
                    <button type="button" className="add-timer-add-subtimer" onClick={() => setSteps((current) => [...current, createEmptyStep()])}>
                        <FontAwesomeIcon icon={faPlus} />
                        <span>{t('addStopwatch.addStep')}</span>
                    </button>
                </div>}

                <div className="add-timer-actions">
                    <button type="button" className="add-timer-cancel" onClick={closeAndReset}>{t('addStopwatch.cancel')}</button>
                    <button type="button" className="add-timer-submit" onClick={handleSubmit} disabled={!title.trim()}>{t('addStopwatch.create')}</button>
                </div>
            </div>
        </div>
    );
};
